import { Table } from '@radix-ui/themes';
import { ActionsBodyCell } from 'components/common/table/actions-cells';
import { BasicCell, CheckboxCell } from 'components/common/table/body-cell';
import { TableContext } from 'components/common/table/context';
import { CheckedContext } from 'contexts/layout/checked.context';
import { CHECKBOX_KEY } from 'enums/tables';
import { useOnContextMenu } from 'hooks/useOnContextMenu';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { RowWithID } from 'interfaces/tables/main';
import { ITableRow } from 'interfaces/tables/rows';
import { ITableSelectable } from 'interfaces/tables/selection';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { WithID } from 'lib_ts/interfaces/common/i-generics';
import { MouseEventHandler, useContext, useMemo } from 'react';

interface IProps
  extends
    Partial<ITableSelectable>,
    Partial<ITableCheckable>,
    Partial<ITableRow> {
  tableID: string;
  index: number;
  item: RowWithID;

  visibleColumns: ITypedTableColumn<any>[];
}

export const TableRow = (props: IProps) => {
  const {
    contextTarget,
    pageData,
    selectedCoord,
    setContextTarget,
    setSelectedCoord,
  } = useContext(TableContext);

  const { isLongPressing, onContextMenuHandlers } = useOnContextMenu({
    longPressEnabled: !!props.enableLongPressContextMenu,
    onContextMenu: () => {
      // the small delay allows for the context menu trigger for the table (which clears the selection) to go through before this happens
      setTimeout(() => {
        console.debug('set context menu target from row', item);
        setContextTarget(item);
      }, 100);
    },
  });

  const { lastCheckedIndex, getChecked, setChecked, setLastCheckedIndex } =
    useContext(CheckedContext);

  const { index, item, tableID } = props;

  const menuID = useMemo(() => `${tableID}-menu-${index}`, [tableID, index]);

  const itemID = useMemo(() => (item as WithID<any>)._id, [item]);

  const classes = useMemo(
    () =>
      StringHelper.classNames([
        props.blockSelect
          ? 'cursor-not-allowed'
          : props.enableSelect
            ? 'cursor-pointer'
            : undefined,
        props.rowClassNameFn?.(item, index),
        isLongPressing ? 'select-none' : undefined,
      ]),
    [
      props.blockSelect,
      props.enableSelect,
      props.rowClassNameFn,
      item,
      isLongPressing,
    ]
  );

  const _onClick: MouseEventHandler = (event) => {
    // console.debug('row onClick triggered', event);

    if (event.shiftKey) {
      // deselect any selected/highlighted UI elements
      document.getSelection()?.removeAllRanges();

      const nextValue = !getChecked(itemID);

      if (lastCheckedIndex === null || lastCheckedIndex === undefined) {
        // This is the first row that was checked so no need to bulk select yet
        setLastCheckedIndex(index);
        props.afterCheckOne?.(itemID, nextValue);
        return;
      }

      const iStart = Math.min(lastCheckedIndex, index);
      const iEnd = Math.max(lastCheckedIndex, index);

      const ids = pageData
        .filter((_, i) => i >= iStart && i <= iEnd)
        .map((m) => m._id);

      setChecked(ids, nextValue);
      setLastCheckedIndex(index);
      props.afterCheckMany?.(ids, nextValue);
      return;
    }

    props.onRowClick?.(item, event);

    if (!props.enableSelect) {
      return;
    }

    setSelectedCoord({
      page: selectedCoord.page,
      index: index,
      _id: props.item._id,
      callbackKey: Date.now(),
    });

    // Use the selected row as the last checked index for shift-click handling (but don't check it off yet)
    setLastCheckedIndex(index);
  };

  return (
    <Table.Row
      className={classes}
      data-checked={getChecked(item._id)}
      data-active={
        // match by identity so a stale selected index can't bleed onto a
        // different row when data shifts (e.g. a new row prepended while
        // skipResetSelected keeps the index pinned). Fall back to index for
        // keyboard navigation, which selects by index and clears _id.
        selectedCoord._id != null
          ? item._id === selectedCoord._id
          : index === selectedCoord.index
      }
      data-context-menu={item === contextTarget}
      data-row-id={itemID}
      data-testid={index}
      data-testlocator={props.rowTestLocatorFn?.(item)}
      onClick={_onClick}
      onMouseEnter={
        props.onRowHover ? () => props.onRowHover?.(item) : undefined
      }
      onMouseLeave={
        props.onRowHover
          ? (e) => {
              const next = e.relatedTarget;
              const body = e.currentTarget.closest('tbody');

              // moving onto another row: that row's enter owns the hover
              if (next instanceof Node && body?.contains(next)) {
                return;
              }

              props.onRowHover?.(undefined);
            }
          : undefined
      }
      {...onContextMenuHandlers}
    >
      {props.visibleColumns.map((col, iCol) => {
        switch (col.key) {
          case CHECKBOX_KEY: {
            return (
              <CheckboxCell
                key={iCol}
                menuID={menuID}
                rowIndex={props.index}
                col={col}
                item={item}
                index={index}
                afterCheckOne={props.afterCheckOne}
                onShiftClick={_onClick}
              />
            );
          }

          default: {
            if (col.actions) {
              /** draw actions menu with an option for each action */
              return (
                <ActionsBodyCell
                  key={iCol}
                  menuID={menuID}
                  rowIndex={props.index}
                  col={col}
                  item={item}
                />
              );
            }

            return (
              <BasicCell
                key={iCol}
                menuID={menuID}
                rowIndex={props.index}
                col={col}
                item={item}
              />
            );
          }
        }
      })}
    </Table.Row>
  );
};
